import React, { useEffect, useRef, useState } from 'react';
import { useP } from '../../i18n/useP';
import { formatNumber, formatDate } from '../../utils/format';

/**
 * Inline editable cell.
 * Display = blue span (user-entered convention, matches Charges sociales pattern).
 * Click → input with blue background. Blur or Enter commits. Esc cancels.
 *
 * Props :
 *   value       : current value (any type)
 *   onCommit    : (newValue) => Promise|void — called when user commits a change
 *   type        : 'text' | 'number' | 'date' | 'select'
 *   options     : [{value, label}] for type='select'
 *   align       : 'left' | 'right' | 'center'
 *   step        : number step (for type='number')
 *   placeholder : string
 *   nullable    : if true, empty value commits null
 *   formatter   : (v) => display string (defaults per type)
 *   parser      : (s) => parsed value to commit (defaults per type)
 *   mutedWhenEmpty : if true, render '—' in muted gray when value is empty
 */
export default function EditableCell({
  value,
  onCommit,
  type = 'text',
  options = [],
  align = 'left',
  step = 1,
  min,
  max,
  placeholder = '',
  nullable = true,
  formatter,
  parser,
  mutedWhenEmpty = true,
}) {
  const p = useP();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editing]);

  function startEdit() {
    setDraft(value == null || value === undefined ? '' : String(value));
    setEditing(true);
  }

  function cancel() { setEditing(false); }

  function commit() {
    setEditing(false);
    const empty = draft === '' || draft == null;
    if (empty) {
      if (!nullable) return;
      if (value == null || value === '') return;
      onCommit(null);
      return;
    }
    let parsed;
    try {
      parsed = parser ? parser(draft) : (type === 'number' ? Number(draft) : draft);
    } catch (e) {
      return;
    }
    if (type === 'number' && typeof parsed === 'number' && !Number.isNaN(parsed)) {
      if (typeof min === 'number' && parsed < min) parsed = min;
      if (typeof max === 'number' && parsed > max) parsed = max;
    }
    if (parsed === value) return;
    // For numbers, compare loosely
    if (type === 'number' && Number(parsed) === Number(value)) return;
    onCommit(parsed);
  }

  // ── Display value ────────────────────────────────────────────────────────
  const isEmpty = value == null || value === '';
  let display = '';
  if (formatter) display = formatter(value);
  else if (isEmpty) display = mutedWhenEmpty ? '—' : '';
  else if (type === 'number') display = formatNumber(value, 0);
  else if (type === 'date') display = formatDate(value);   // Swiss dd-mm-yyyy
  else if (type === 'select') {
    const opt = options.find(o => String(o.value) === String(value));
    display = opt ? opt.label : String(value);
  } else display = String(value);

  if (editing) {
    const sharedProps = {
      ref: inputRef,
      className: 'hr-edit-input',
      style: { textAlign: align, width: '100%' },
      value: draft,
      onChange: e => setDraft(e.target.value),
      onBlur: commit,
      onKeyDown: e => {
        if (e.key === 'Enter') e.target.blur();
        else if (e.key === 'Escape') { cancel(); }
      },
      placeholder,
    };
    if (type === 'select') {
      return (
        <select {...sharedProps}>
          <option value="">{p('— (aucun)')}</option>
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      );
    }
    if (type === 'number') {
      return <input type="number" step={step} min={min} max={max} {...sharedProps} />;
    }
    if (type === 'date') {
      return (
        <DateTripleInput
          value={draft}
          onCommitIso={iso => {
            // Sync draft so the parent's `commit` logic runs through; we
            // call onCommit directly to bypass the text-comparison path.
            setDraft(iso == null ? '' : iso);
            setEditing(false);
            if (iso === null) {
              if (nullable && value != null && value !== '') onCommit(null);
              return;
            }
            if (iso === value) return;
            onCommit(iso);
          }}
          onCancel={cancel}
        />
      );
    }
    return <input type="text" {...sharedProps} />;
  }

  // (early-return handled above; reuse cls below)
  const cls = isEmpty && mutedWhenEmpty
    ? 'hr-edit-cell hr-edit-cell--empty'
    : 'hr-edit-cell';
  return (
    <span
      className={cls}
      style={{ textAlign: align }}
      onClick={startEdit}
      title={p('Cliquer pour modifier')}
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') startEdit(); }}
    >
      {display}
    </span>
  );
}


/**
 * 3-segment date input (DD - MM - YYYY) with auto-advance and Swiss display.
 * Calls `onCommitIso(isoString | null)` when the user finishes (blur outside
 * the trio, Enter, or all 3 segments filled). `null` = clear.
 */
function DateTripleInput({ value, onCommitIso, onCancel }) {
  // Parse incoming ISO yyyy-mm-dd → 3 segments
  const init = parseIso(value);
  const [dd, setDd] = useState(init.dd);
  const [mm, setMm] = useState(init.mm);
  const [yy, setYy] = useState(init.yy);
  const ddRef = useRef(null);
  const mmRef = useRef(null);
  const yyRef = useRef(null);

  useEffect(() => {
    ddRef.current?.focus();
    ddRef.current?.select();
  }, []);

  function digits(s, max) {
    return s.replace(/\D/g, '').slice(0, max);
  }

  function commitFromState(d, m, y) {
    // all empty → clear
    if (!d && !m && !y) { onCommitIso(null); return; }
    // partial → cancel (don't commit broken date)
    if (!d || !m || !y || y.length < 4) { onCancel(); return; }
    const padD = d.padStart(2, '0');
    const padM = m.padStart(2, '0');
    const iso = `${y}-${padM}-${padD}`;
    // Sanity check the date
    const parsed = new Date(iso);
    if (isNaN(parsed.getTime())) { onCancel(); return; }
    onCommitIso(iso);
  }

  function onKey(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    } else if (e.key === 'Backspace' && e.target.value === '') {
      // empty segment + backspace → focus previous
      if (e.target === mmRef.current) ddRef.current?.focus();
      else if (e.target === yyRef.current) mmRef.current?.focus();
    }
  }

  // Use a single shared blur handler: commit only if focus leaves the trio.
  function onBlur() {
    setTimeout(() => {
      const active = document.activeElement;
      if (active !== ddRef.current && active !== mmRef.current && active !== yyRef.current) {
        commitFromState(dd, mm, yy);
      }
    }, 0);
  }

  return (
    <span className="hr-date-trio">
      <input
        ref={ddRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        value={dd}
        placeholder="dd"
        onChange={e => {
          const v = digits(e.target.value, 2);
          setDd(v);
          if (v.length === 2) mmRef.current?.focus();
        }}
        onFocus={e => e.target.select()}
        onKeyDown={onKey}
        onBlur={onBlur}
      />
      <span className="hr-date-trio__sep">-</span>
      <input
        ref={mmRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={2}
        value={mm}
        placeholder="mm"
        onChange={e => {
          const v = digits(e.target.value, 2);
          setMm(v);
          if (v.length === 2) yyRef.current?.focus();
        }}
        onFocus={e => e.target.select()}
        onKeyDown={onKey}
        onBlur={onBlur}
      />
      <span className="hr-date-trio__sep">-</span>
      <input
        ref={yyRef}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={4}
        value={yy}
        placeholder="yyyy"
        onChange={e => {
          const v = digits(e.target.value, 4);
          setYy(v);
          if (v.length === 4) {
            // Year complete → commit immediately
            commitFromState(dd, mm, v);
          }
        }}
        onFocus={e => e.target.select()}
        onKeyDown={onKey}
        onBlur={onBlur}
      />
    </span>
  );
}


function parseIso(v) {
  if (!v || typeof v !== 'string') return { dd: '', mm: '', yy: '' };
  const m = v.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return { dd: '', mm: '', yy: '' };
  return { yy: m[1], mm: m[2], dd: m[3] };
}
